// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useState, type ReactNode } from "react";
import { isSignedOutBannerMounted } from "../components/layout/SignedOutBanner";
import { currentRouteUrl, navigateTo } from "../router/navigation";
import { FullPageLoader } from "./FullPageLoader";
import { rememberReturnTo } from "./returnTo";
import { useAuthGate } from "./useAuthGate";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthorized, isProcessing, isCheckingSession, isSessionExpired } = useAuthGate();
  const isReady = !isProcessing && isAuthorized && !isCheckingSession;
  const [hasEntered, setHasEntered] = useState(false);
  useEffect(() => {
    if (isReady) {
      setHasEntered(true);
    }
  }, [isReady]);
  // Issue 910 (RiddleDesign ruling): a session that expires while the user is
  // working keeps the page, and its unsaved input, on screen under the
  // "You're signed out." bar instead of bouncing to the sign-in page.
  const keepPageWhileSignedOut =
    hasEntered && isSessionExpired && isSignedOutBannerMounted();
  const mustSignIn = !isProcessing && !isAuthorized && !keepPageWhileSignedOut;

  useEffect(() => {
    if (mustSignIn) {
      rememberReturnTo(currentRouteUrl());
      navigateTo("/", { replace: true });
    }
  }, [mustSignIn]);

  if (keepPageWhileSignedOut) {
    return <>{children}</>;
  }

  if (isProcessing || !isAuthorized || isCheckingSession) {
    return <FullPageLoader />;
  }

  return <>{children}</>;
}
